/**
 * Thin fetch wrapper for HTTP API. Cookies are sent automatically.
 *
 * `jsonFetch` surfaces a parsed `{ error: "..." }` body as the thrown
 * message when the server returns a 4xx/5xx — callers get a readable
 * string instead of `"400 Bad Request: { "error": ... }"`.
 */

import type {
  ApiAdminAuditResponse,
  ApiAdminBansResponse,
  ApiAdminChatResponse,
  ApiAdminMe,
  ApiAdminPlayersResponse,
  ApiAuthResult,
  ApiCreateLobby,
  ApiLobbyList,
  ApiMe,
  ApiSetCardPrefs,
  LobbyDetail,
} from "../../../shared/protocol.js";

async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...init,
  });
  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body && typeof body === "object" && typeof (body as any).error === "string") {
        message = (body as any).error;
      }
    } catch {
      // Body wasn't JSON; fall through to the default message.
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

export async function fetchMe(): Promise<ApiMe> {
  return jsonFetch<ApiMe>("/api/me");
}

export async function setMyName(displayName: string): Promise<ApiMe> {
  return jsonFetch<ApiMe>("/api/me/name", {
    method: "POST",
    body: JSON.stringify({ displayName }),
  });
}

export async function setMyCardPrefs(prefs: ApiSetCardPrefs): Promise<ApiMe> {
  return jsonFetch<ApiMe>("/api/me/cards", {
    method: "POST",
    body: JSON.stringify(prefs),
  });
}

export async function signupAccount(username: string, password: string): Promise<ApiAuthResult> {
  return jsonFetch<ApiAuthResult>("/api/auth/signup", {
    method: "POST",
    body: JSON.stringify({ username, password }),
  });
}

export async function loginAccount(username: string, password: string): Promise<ApiAuthResult> {
  return jsonFetch<ApiAuthResult>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ username, password }),
  });
}

export async function logoutAccount(): Promise<ApiAuthResult> {
  return jsonFetch<ApiAuthResult>("/api/auth/logout", { method: "POST" });
}

export async function listLobbies() {
  const r = await jsonFetch<ApiLobbyList>("/api/lobbies");
  return r.lobbies;
}

export async function createLobby(body: ApiCreateLobby): Promise<LobbyDetail> {
  const r = await jsonFetch<{ lobby: LobbyDetail }>("/api/lobbies", {
    method: "POST",
    body: JSON.stringify(body),
  });
  return r.lobby;
}

export async function fetchLobbyByCode(code: string): Promise<LobbyDetail> {
  const r = await jsonFetch<{ lobby: LobbyDetail }>(`/api/lobbies/by-code/${encodeURIComponent(code)}`);
  return r.lobby;
}

export async function fetchLobby(id: string): Promise<LobbyDetail> {
  const r = await jsonFetch<{ lobby: LobbyDetail }>(`/api/lobbies/${encodeURIComponent(id)}`);
  return r.lobby;
}

// ---------------------------------------------------------------------------
// Admin / moderation
// ---------------------------------------------------------------------------

export async function adminMe(): Promise<ApiAdminMe> {
  return jsonFetch<ApiAdminMe>("/api/admin/me");
}

export async function adminListPlayers(limit = 100): Promise<ApiAdminPlayersResponse> {
  return jsonFetch<ApiAdminPlayersResponse>(`/api/admin/players?limit=${limit}`);
}

export async function adminListBans(): Promise<ApiAdminBansResponse> {
  return jsonFetch<ApiAdminBansResponse>("/api/admin/bans");
}

export async function adminBan(
  kind: "player" | "ip",
  value: string,
  reason?: string,
): Promise<{ ban: import("../../../shared/protocol.js").AdminBan }> {
  return jsonFetch("/api/admin/bans", {
    method: "POST",
    body: JSON.stringify({ kind, value, reason }),
  });
}

export async function adminRemoveBan(kind: "player" | "ip", value: string): Promise<{ removed: boolean }> {
  return jsonFetch(`/api/admin/bans/${encodeURIComponent(kind)}/${encodeURIComponent(value)}`, {
    method: "DELETE",
  });
}

export async function adminMute(
  playerId: string,
  durationSec: number,
  reason?: string,
): Promise<{ mutedUntil: number | null }> {
  return jsonFetch("/api/admin/mute", {
    method: "POST",
    body: JSON.stringify({ playerId, durationSec, reason }),
  });
}

export async function adminPromote(playerId: string, isAdmin: boolean): Promise<{ ok: true }> {
  return jsonFetch("/api/admin/promote", {
    method: "POST",
    body: JSON.stringify({ playerId, isAdmin }),
  });
}

export async function adminListAudit(limit = 200): Promise<ApiAdminAuditResponse> {
  return jsonFetch<ApiAdminAuditResponse>(`/api/admin/audit?limit=${limit}`);
}

export async function adminListChat(opts: { playerId?: string; limit?: number } = {}): Promise<ApiAdminChatResponse> {
  const params = new URLSearchParams();
  if (opts.playerId) params.set("playerId", opts.playerId);
  if (opts.limit) params.set("limit", String(opts.limit));
  const qs = params.toString();
  return jsonFetch<ApiAdminChatResponse>(`/api/admin/chat${qs ? `?${qs}` : ""}`);
}
